import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { FREEMIUM_ENABLED, OFICIO_ENABLED } from "../config/features";
import EncuestaIndividualDesactivada from "./EncuestaIndividualDesactivada";
import FormSelect from "../components/FormSelect";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { detectCountry, iniciarEncuestaFreemium } from "../utils/surveyApi";
import { countryFlag, detectCountryFromBrowser } from "../utils/country";
import { PAISES, GENEROS, OFICIOS } from "../data/paises";
import logoInstituto from "../assets/logo-instituto-negro.png";
import styles from "../styles/encuesta4c.module.css";

const PAISES_SET = new Set(PAISES.map((p) => p.code));
const PRIVACY_POLICY_URL =
  "https://www.instituto8.org/politica-de-privacidad-y-cookies/";

function localizedCountryName(code, fallbackName, language) {
  try {
    const lng = (language || "es").slice(0, 2);
    const dn = new Intl.DisplayNames([lng], { type: "region" });
    return dn.of(code) || fallbackName;
  } catch {
    return fallbackName;
  }
}

function InicioEncuesta() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [pais, setPais] = useState(() => detectCountryFromBrowser());
  const [genero, setGenero] = useState("");
  const [oficio, setOficio] = useState("");
  const [aceptaPrivacidad, setAceptaPrivacidad] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const paisTouchedRef = useRef(false);
  const year = new Date().getFullYear();

  const paisOptions = useMemo(
    () =>
      PAISES.map((p) => ({
        value: p.code,
        label: `${countryFlag(p.code)} ${localizedCountryName(
          p.code,
          p.name,
          i18n.language
        )}`.trim(),
      })),
    [i18n.language]
  );

  const generoOptions = useMemo(
    () =>
      GENEROS.map((g) => ({
        value: g.value,
        label: t(`form.genders.${g.value}`),
      })),
    [t]
  );

  const oficioOptions = useMemo(
    () =>
      OFICIOS.map((o) => ({
        value: o.value,
        label: t(`form.roles.${o.value}`),
      })),
    [t]
  );

  useEffect(() => {
    let cancelled = false;
    detectCountry().then((code) => {
      if (cancelled || !code || !PAISES_SET.has(code) || paisTouchedRef.current) return;
      setPais(code);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!FREEMIUM_ENABLED) {
    return <EncuestaIndividualDesactivada />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!nombre.trim() || !email.trim() || !pais || !genero || (OFICIO_ENABLED && !oficio)) {
      setError(t("form.incomplete"));
      return;
    }

    if (!aceptaPrivacidad) {
      setError(t("form.privacyRequired"));
      return;
    }

    setLoading(true);
    const result = await iniciarEncuestaFreemium({
      nombre: nombre.trim(),
      email: email.trim(),
      pais,
      genero,
      oficio: OFICIO_ENABLED ? oficio : undefined,
      privacidad: true,
    });
    setLoading(false);

    if (!result.ok) {
      setError(t(result.messageKey || "errors.startFailed"));
      return;
    }

    navigate(`/encuesta?token=${encodeURIComponent(result.token)}`);
  };

  return (
    <div className={`${styles.root} ${styles.layout}`}>
      <header className={styles.header}>
        <img src={logoInstituto} alt={t("common.logoAlt")} className={styles.logo} />
      </header>

      <main className={styles.main}>
        <div className={styles.welcomeWrap}>
          <h1 className={styles.welcomeTitle}>
            {t("form.title")}{" "}
            <span className={styles.welcomeTitleHighlight}>
              {t("form.titleHighlight")}
            </span>
          </h1>
          <p className={styles.formLead}>{t("form.lead")}</p>

          <form
            className={`${styles.card} ${styles.cardShadow} ${styles.formCard}`}
            onSubmit={handleSubmit}
          >
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="nombre">
                {t("form.name")}
              </label>
              <input
                id="nombre"
                type="text"
                className={styles.formInput}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder={t("form.namePlaceholder")}
                autoComplete="given-name"
                disabled={loading}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="email">
                {t("form.email")}
              </label>
              <input
                id="email"
                type="email"
                className={styles.formInput}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("form.emailPlaceholder")}
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <FormSelect
              id="pais"
              label={t("form.country")}
              value={pais}
              onChange={(code) => {
                paisTouchedRef.current = true;
                setPais(code);
              }}
              options={paisOptions}
              placeholder={t("form.countryPlaceholder")}
              disabled={loading}
              searchable
            />

            <FormSelect
              id="genero"
              label={t("form.gender")}
              value={genero}
              onChange={setGenero}
              options={generoOptions}
              placeholder={t("form.genderPlaceholder")}
              disabled={loading}
            />

            {OFICIO_ENABLED && (
              <FormSelect
                id="oficio"
                label={t("form.role")}
                value={oficio}
                onChange={setOficio}
                options={oficioOptions}
                placeholder={t("form.rolePlaceholder")}
                disabled={loading}
              />
            )}

            <label
              className={`${styles.privacyRow} ${
                error === t("form.privacyRequired") ? styles.privacyRowInvalid : ""
              }`}
            >
              <input
                id="privacidad"
                type="checkbox"
                className={styles.privacyInput}
                checked={aceptaPrivacidad}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAceptaPrivacidad(checked);
                  if (checked) {
                    setError((current) =>
                      current === t("form.privacyRequired") ? "" : current
                    );
                  }
                }}
                disabled={loading}
              />
              <span className={styles.privacyMark} aria-hidden="true">
                <svg viewBox="0 0 16 16" width="12" height="12">
                  <path
                    d="M3.5 8.2 6.4 11 12.5 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span className={styles.privacyText}>
                <Trans
                  i18nKey="form.privacyLabel"
                  components={{
                    policy: (
                      <a
                        href={PRIVACY_POLICY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.privacyLink}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ),
                  }}
                />
              </span>
            </label>

            {error && <p className={styles.formError}>{error}</p>}

            <button
              type="submit"
              className={styles.btnPrimary}
              disabled={loading}
            >
              {loading ? t("form.submitting") : t("form.submit")}
            </button>
          </form>
        </div>
      </main>

      <footer className={styles.footer}>{t("common.footer", { year })}</footer>
      <LanguageSwitcher />
    </div>
  );
}

export default InicioEncuesta;
